<script setup lang="ts">
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { EasePack } from 'gsap/EasePack'
import type { MenuItemRecord } from '~/types/datocms'

const route = useRoute()
const NuxtLink = resolveComponent('NuxtLink')
const { menuHide } = useMenu()

const props = defineProps({
  data: {
    type: Object as PropType<MenuItemRecord>,
    default: () => {}
  },
  index: {
    type: Number,
    required: true
  }
})

function onClick() {
  menuHide()
}

const ctxRef = ref<HTMLElement | null>(null)
let tl: gsap.core.Timeline | undefined = undefined

onMounted(() => {
  nextTick(() => {
    if (ctxRef.value) {
      gsap.registerPlugin(ScrollTrigger, EasePack)

      const items = gsap.utils.toArray(`.fx-menu-item-${props.index}`)
      if (!items.length) return

      tl = useHorizontalLoop(items, {
        paused: true,
        speed: 0.5,
        repeat: -1,
        reversed: props.index % 2 === 0
      })

      if (props.data.reference !== null && route.params.slug !== props.data.reference?.slug) {
        if (props.index % 2 === 0) {
          tl?.reverse()
        } else {
          tl?.play()
        }
      }
    }
  })
})

onUnmounted(() => {
  tl?.kill()
  tl = undefined
})
</script>

<template>
  <component
    :is="data.reference !== null ? NuxtLink : data.externalLink ? NuxtLink : 'button'"
    ref="ctxRef"
    :to="
      data.reference
        ? useLocalePathByType(data.reference._modelApiKey, data.reference?.slug!)
        : data.externalLink
    "
    :type="data.reference === null || !data.externalLink ? 'button' : undefined"
    class="flex py-1.5 text-4xl font-light uppercase tracking-wide text-yellow transition-colors duration-300 lg:text-6xl"
    :class="[
      {
        '!text-black': data.reference !== null && $route.params.slug === data.reference?.slug!
      },
      data.reference !== null || data.externalLink
        ? 'cursor-pointer'
        : Array.isArray(data.children) && data.children.length
          ? 'cursor-pointer'
          : 'cursor-auto'
    ]"
    @mouseenter="tl?.pause()"
    @mouseleave="
      data.reference !== null && $route.params.slug === data.reference?.slug!
        ? undefined
        : props.index % 2 === 0
          ? tl?.reverse()
          : tl?.play()
    "
    @click="onClick()"
  >
    <span
      v-for="(n, idx) in 20"
      :key="idx"
      :data="data"
      class="px-2 lg:px-5"
      :class="`fx-menu-item-${props.index}`"
    >
      {{ data.title }}
    </span>
  </component>
</template>
